import type { MedplumClient } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import React, { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Badge,
  Button,
  Heading,
  Icon,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Text,
  cn,
  toast,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useUserName } from '@eluve/smart-blocks';

const AGENT_TAG_SYSTEM = 'https://eluve.com/agent';
const ON_BEHALF_OF_SYSTEM = 'https://eluve.com/on-behalf-of-user';
/** Identifier system tying a DiagnosticReport back to its source upload. */
const EXTRACTED_FROM_SYSTEM = 'https://eluve.com/extracted-from';
/** The lab-results app id (stable; see agent-registry.ts). */
const AGENT_ID = 'extract-document';

interface ObservationRow {
  id: string;
  code: string;
  value: string;
  referenceRange: string | null;
  effectiveDateTime: string | null;
  status: string | null;
  issued: string | null;
  agentId: string | null;
  onBehalfOfUserId: string | null;
  derivedFromRef: string | null;
}

interface DocumentGroup {
  /** `DiagnosticReport/<id>` — one group per extracted lab report. */
  key: string;
  /** The grouping DiagnosticReport this batch belongs to. */
  diagnosticReportId: string;
  /** The source upload the report was extracted from, if resolvable. */
  documentReferenceId: string | null;
  title: string;
  /** Detected FHIR entity kind for this group, e.g. "Lab report". */
  kind: string;
  /** Clinical date of the report (DiagnosticReport.effectiveDateTime), if stated. */
  reportDate: string | null;
  /** When the agent produced this report (DiagnosticReport.issued). */
  extractedAt: string | null;
  /** The report's result Observations, kept so edits can patch the full resource. */
  observations: Observation[];
}

/** Per-Observation editable fields, populated when a group enters edit mode. */
interface EditDraft {
  code: string;
  /** Drives whether we edit a numeric quantity or a free-text value. */
  valueKind: 'quantity' | 'string';
  valueNumber: string;
  unit: string;
  valueString: string;
  referenceRange: string;
  effectiveDateTime: string;
}

const tagValue = (obs: Observation, system: string): string | null =>
  obs.meta?.tag?.find((t) => t.system === system)?.code ?? null;

const formatValue = (obs: Observation): string => {
  if (obs.valueQuantity) {
    const { value, unit } = obs.valueQuantity;
    return unit ? `${value} ${unit}` : `${value}`;
  }
  return obs.valueString ?? '—';
};

/**
 * Display a clinical effective date as its plain calendar day. A lab
 * effective date is date-only (no time/zone), but `formatDate.toDisplay`
 * re-zones to a fixed -07:00 offset and would shift a `YYYY-MM-DD` value back a
 * day in eastern timezones. Anchor the same Y-M-D at midday UTC so no offset
 * crosses the day boundary — this keeps the displayed date identical to the
 * edit date-input (which shows `value.slice(0, 10)`).
 */
const formatEffectiveDate = (value: string): string =>
  formatDate.toDisplay(`${value.slice(0, 10)}T12:00:00Z`);

/**
 * Parse a free-text reference range into numeric bounds. Handles the common
 * lab formats: `low–high [unit]`, `< X` / `≤ X` (upper bound only), and
 * `> X` / `≥ X` (lower bound only). Returns null if no bounds can be read.
 */
const parseRange = (
  text: string | null,
): { low: number | null; high: number | null } | null => {
  if (!text) return null;
  const t = text.replace(/[–—]/g, '-');
  const upper = t.match(/[<≤]\s*=?\s*(-?\d+(?:\.\d+)?)/);
  if (upper) return { low: null, high: Number.parseFloat(upper[1] ?? '') };
  const lower = t.match(/[>≥]\s*=?\s*(-?\d+(?:\.\d+)?)/);
  if (lower) return { low: Number.parseFloat(lower[1] ?? ''), high: null };
  const range = t.match(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/);
  if (range) {
    return {
      low: Number.parseFloat(range[1] ?? ''),
      high: Number.parseFloat(range[2] ?? ''),
    };
  }
  return null;
};

/** Whether a numeric observation reads above/below its reference range, for
 *  the out-of-range highlight. Non-numeric values (valueString) never flag. */
const rangeStatus = (obs: Observation): 'high' | 'low' | null => {
  const value = obs.valueQuantity?.value;
  if (typeof value !== 'number') return null;
  const r = parseRange(obs.referenceRange?.[0]?.text ?? null);
  if (!r) return null;
  if (r.high != null && value > r.high) return 'high';
  if (r.low != null && value < r.low) return 'low';
  return null;
};

const derivedDocRef = (obs: Observation): string | null =>
  obs.derivedFrom?.find((r) => r.reference?.startsWith('DocumentReference/'))
    ?.reference ?? null;

const toRow = (obs: Observation): ObservationRow => ({
  id: obs.id ?? '',
  code: obs.code?.text ?? 'Unnamed observation',
  value: formatValue(obs),
  referenceRange: obs.referenceRange?.[0]?.text ?? null,
  effectiveDateTime: obs.effectiveDateTime ?? null,
  status: obs.status ?? null,
  issued: obs.issued ?? null,
  agentId: tagValue(obs, AGENT_TAG_SYSTEM),
  onBehalfOfUserId: tagValue(obs, ON_BEHALF_OF_SYSTEM),
  derivedFromRef: derivedDocRef(obs),
});

const draftFromObservation = (obs: Observation): EditDraft => ({
  code: obs.code?.text ?? '',
  valueKind: obs.valueQuantity ? 'quantity' : 'string',
  valueNumber:
    obs.valueQuantity?.value !== undefined
      ? String(obs.valueQuantity.value)
      : '',
  unit: obs.valueQuantity?.unit ?? '',
  valueString: obs.valueString ?? '',
  referenceRange: obs.referenceRange?.[0]?.text ?? '',
  effectiveDateTime: obs.effectiveDateTime ?? '',
});

/**
 * Resolves an Eluve user id to a display name. Rendered only inside the
 * (i) popover, so the GraphQL lookup is lazy — it fires the first time a
 * user opens the popover, not on table render.
 */
const OnBehalfOfName: React.FC<{ userId: string }> = ({ userId }) => {
  const name = useUserName(userId, userId.slice(0, 8));
  return <>{name}</>;
};

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-[7rem_1fr] gap-2">
    <Text size="xs" color="tertiary" className="uppercase">
      {label}
    </Text>
    <div className="text-xs">{children}</div>
  </div>
);

/**
 * The Observation's FHIR version history (oldest → newest) — the agent's
 * `preliminary` draft through the human's review. Fetched lazily on popover open.
 */
const ObservationHistory: React.FC<{
  client: MedplumClient;
  observationId: string;
}> = ({ client, observationId }) => {
  const [versions, setVersions] = useState<Observation[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    client
      .readHistory('Observation', observationId)
      .then((bundle) => {
        if (cancelled) return;
        const vs = (bundle.entry ?? [])
          .map((e) => e.resource as Observation | undefined)
          .filter((r): r is Observation => Boolean(r))
          .reverse(); // history comes newest-first; show oldest-first
        setVersions(vs);
      })
      .catch(() => {
        if (!cancelled) setVersions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [client, observationId]);

  if (!versions)
    return (
      <Text size="xs" color="tertiary">
        Loading…
      </Text>
    );
  if (versions.length === 0)
    return (
      <Text size="xs" color="tertiary">
        —
      </Text>
    );

  const statusVariant = (
    status?: string,
  ): 'secondary' | 'success' | 'destructive' =>
    status === 'final'
      ? 'success'
      : status === 'entered-in-error'
        ? 'destructive'
        : 'secondary';

  return (
    <Stack gap={2} align="stretch">
      {versions.map((v, i) => {
        const agent = v.meta?.tag?.find(
          (t) => t.system === AGENT_TAG_SYSTEM,
        )?.code;
        const actor =
          v.status === 'preliminary' && agent
            ? `${agent} (agent)`
            : (v.meta?.author?.display ??
              v.meta?.author?.reference ??
              'unknown');
        const when = v.meta?.lastUpdated
          ? formatDate.toLocalDateTime(v.meta.lastUpdated)
          : '';
        return (
          <div
            key={v.meta?.versionId ?? i}
            className="w-full rounded-md border border-border-secondary p-2"
          >
            <div className="flex items-center gap-2">
              <Text size="sm" weight="medium">
                {formatValue(v)}
              </Text>
              <Badge
                variant={statusVariant(v.status)}
                className="ml-auto shrink-0 whitespace-nowrap uppercase"
              >
                {v.status}
              </Badge>
            </div>
            <Text size="xs" color="tertiary" className="mt-1 block">
              {actor}
              {when ? ` · ${when}` : ''}
            </Text>
          </div>
        );
      })}
    </Stack>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Text
    size="xs"
    color="tertiary"
    weight="medium"
    className="mb-2 block tracking-wide uppercase"
  >
    {children}
  </Text>
);

interface AgentGrantInfo {
  agent: {
    displayName: string;
    publisher: string;
    trustLevel: 'first-party' | 'third-party';
    logoUrl: string | null;
  };
  scopesPlainEnglish: string[];
}

/**
 * Agent identity for the provenance panel — display name, trust level, and the
 * plain-English permissions, fetched lazily from the mini-apps `grant` endpoint
 * (the same source the consent modal uses). Falls back to the raw agent id
 * while loading or if the lookup fails.
 */
const AgentProvenance: React.FC<{ agentId: string | null }> = ({ agentId }) => {
  const apiClient = useApiClient();
  const tenantId = useAssignedTenantIdFromParams();
  const [info, setInfo] = useState<AgentGrantInfo | null>(null);

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    apiClient.miniApps
      .grant({ params: { tenantId, agentId } })
      .then((res) => {
        if (!cancelled && res.status === 200) {
          setInfo({
            agent: res.body.agent,
            scopesPlainEnglish: res.body.scopesPlainEnglish,
          });
        }
      })
      .catch(() => {
        /* fall back to the raw id */
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId, agentId]);

  const agent = info?.agent;
  return (
    <>
      <MetaRow label="Agent">
        {agentId ? (
          <Stack gap={0}>
            <Text size="xs" weight="medium">
              {agent?.displayName ?? agentId}
            </Text>
            {agent && (
              <Text size="xs" color="tertiary">
                by {agent.publisher}
              </Text>
            )}
          </Stack>
        ) : (
          '—'
        )}
      </MetaRow>
      {info && info.scopesPlainEnglish.length > 0 && (
        <MetaRow label="Permissions">
          <ul className="list-disc space-y-0.5 pl-4">
            {info.scopesPlainEnglish.map((scope) => (
              <li key={scope}>
                <Text size="xs">{scope}</Text>
              </li>
            ))}
          </ul>
        </MetaRow>
      )}
    </>
  );
};

/**
 * (i) trigger + right-side Sheet holding provenance + the chain-of-effects
 * history for one Observation. SheetContent unmounts while closed (Radix
 * default), so the `OnBehalfOfName` lookup and the history fetch stay lazy.
 */
const ObservationDetailsSheet: React.FC<{
  client: MedplumClient;
  row: ObservationRow;
  documentTitle: string;
}> = ({ client, row, documentTitle }) => (
  <Sheet>
    <SheetTrigger asChild>
      <button
        type="button"
        aria-label="Observation details"
        className="text-text-tertiary hover:text-text-primary"
      >
        <Icon name="Info" size="sm" />
      </button>
    </SheetTrigger>
    <SheetContent side="right" className="w-[30rem] overflow-y-auto">
      <SheetHeader>
        <SheetTitle>{row.code}</SheetTitle>
        <Stack direction="horizontal" align="center" gap={2}>
          <Text size="md" weight="semibold">
            {row.value}
          </Text>
          {row.status && (
            <Badge
              variant={row.status === 'final' ? 'success' : 'secondary'}
              className="shrink-0 whitespace-nowrap uppercase"
            >
              {row.status}
            </Badge>
          )}
        </Stack>
      </SheetHeader>

      <div className="flex flex-col gap-6 px-4 pb-6">
        <section>
          <SectionLabel>Provenance</SectionLabel>
          <Stack gap={2}>
            <MetaRow label="On behalf of">
              {row.onBehalfOfUserId ? (
                <Stack gap={0}>
                  <Text size="xs" weight="medium">
                    <OnBehalfOfName userId={row.onBehalfOfUserId} />
                  </Text>
                  <Text
                    size="xs"
                    color="tertiary"
                    className="font-mono break-all"
                  >
                    {row.onBehalfOfUserId}
                  </Text>
                </Stack>
              ) : (
                '—'
              )}
            </MetaRow>
            <AgentProvenance agentId={row.agentId} />
            <MetaRow label="Issued">
              {row.issued ? formatDate.toLocalDateTime(row.issued) : '—'}
            </MetaRow>
            <MetaRow label="Source">
              <Stack gap={0}>
                <Text size="xs">{documentTitle}</Text>
                {row.derivedFromRef && (
                  <Text
                    size="xs"
                    color="tertiary"
                    className="font-mono break-all"
                  >
                    {row.derivedFromRef}
                  </Text>
                )}
              </Stack>
            </MetaRow>
            <MetaRow label="Observation">
              <Text size="xs" color="tertiary" className="font-mono break-all">
                Observation/{row.id}
              </Text>
            </MetaRow>
          </Stack>
        </section>

        <section>
          <SectionLabel>Chain of effects</SectionLabel>
          <ObservationHistory client={client} observationId={row.id} />
        </section>
      </div>
    </SheetContent>
  </Sheet>
);

export interface ExtractedObservationsProps {
  client: MedplumClient;
  fhirPatientId: string;
  /**
   * When set, scope the view to the single source document with this
   * DocumentReference id (matched on the `extracted-from` identifier) instead
   * of listing every extracted report for the patient. Used by the per-file
   * "View Details" modal.
   */
  documentReferenceId?: string;
  /**
   * "List" mode: when provided, a report that needs review shows a single
   * **Review** action (calling this with the report's source document id)
   * instead of inline Edit/Reject/Approve — review then happens in the
   * side-by-side modal. When omitted, this *is* that review surface and the
   * inline actions are shown.
   */
  onReview?: (documentReferenceId: string) => void;
}

/**
 * Lists the patient's agent-extracted reports (one group per `eluve.com/agent`
 * DiagnosticReport) and lets a clinician review each draft.
 *
 * Two-phase provenance: the agent writes `preliminary` (path A); the human
 * approves (→ `final`) or rejects (→ `entered-in-error`), optionally editing
 * first. Approval is sent as *intent* to the attest endpoint, which flips the
 * statuses and writes the Provenance — the frontend never authors it.
 */
export const ExtractedObservations: React.FC<ExtractedObservationsProps> = ({
  client,
  fhirPatientId,
  documentReferenceId: scopeDocId,
  onReview,
}) => {
  const [groups, setGroups] = useState<DocumentGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  /** Group key currently in edit mode, or null. Only one group edits at a time. */
  const [editingKey, setEditingKey] = useState<string | null>(null);
  /** Observation id → working edit draft, while a group is in edit mode. */
  const [drafts, setDrafts] = useState<Record<string, EditDraft>>({});
  /** Group key with an approve/reject in flight, to disable its controls. */
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** Group keys currently expanded. Empty = every report starts collapsed, so
   *  the patient's reports read as a scannable list of headers. */
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const apiClient = useApiClient();
  const tenantId = useAssignedTenantIdFromParams();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // The grouping unit is the DiagnosticReport. List the patient's
      // agent-extracted reports (active only — superseded / rejected reports
      // are entered-in-error and hidden), then fetch each report's result
      // Observations.
      const reports = await client.searchResources('DiagnosticReport', {
        subject: `Patient/${fhirPatientId}`,
        _count: 100,
        _sort: '-_lastUpdated',
      });

      const agentReports = reports.filter(
        (r) =>
          r.status !== 'entered-in-error' &&
          r.meta?.tag?.some((t) => t.system === AGENT_TAG_SYSTEM) &&
          // When scoped to one source document, keep only its report(s).
          (!scopeDocId ||
            r.identifier?.some(
              (i) =>
                i.system === EXTRACTED_FROM_SYSTEM && i.value === scopeDocId,
            )),
      );

      const nextGroups: DocumentGroup[] = await Promise.all(
        agentReports.map(async (report) => {
          const resultIds = (report.result ?? [])
            .map((ref) => ref.reference?.replace('Observation/', ''))
            .filter((id): id is string => Boolean(id));

          let observations: Observation[] = [];
          if (resultIds.length > 0) {
            try {
              const fetched = await client.searchResources('Observation', {
                _id: resultIds.join(','),
                _count: resultIds.length,
              });
              // A FHIR `_id=a,b,c` search returns matches in the server's
              // default sort, NOT the order the ids were listed — so restore
              // the report's `result[]` order (which the agent wrote in source-
              // document order) instead of rendering Medplum's id ordering.
              const byId = new Map(fetched.map((o) => [o.id, o]));
              observations = resultIds.flatMap((id) => {
                const o = byId.get(id);
                return o ? [o] : [];
              });
            } catch {
              observations = [];
            }
          }

          const documentReferenceId =
            report.identifier?.find((i) => i.system === EXTRACTED_FROM_SYSTEM)
              ?.value ?? null;

          // Detect the FHIR entity kind from the report's category. Today the
          // only producer is the lab app (category LAB); other future apps
          // (imaging, notes) would surface a different kind here.
          const isLab = (report.category ?? []).some((c) =>
            c.coding?.some((co) => co.code === 'LAB'),
          );

          return {
            key: `DiagnosticReport/${report.id}`,
            diagnosticReportId: report.id!,
            documentReferenceId,
            title: report.code?.text ?? 'Laboratory report',
            kind: isLab ? 'Lab report' : 'Extracted record',
            reportDate: report.effectiveDateTime ?? null,
            extractedAt: report.issued ?? null,
            observations,
          };
        }),
      );

      setGroups(nextGroups);
      // In the single-document review modal, auto-expand so the reviewer sees
      // the values immediately next to the source doc. The all-reports list
      // (unscoped) stays collapsed so it reads as a scannable set of headers.
      if (scopeDocId) {
        setExpandedKeys(new Set(nextGroups.map((g) => g.key)));
      }
    } catch {
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, [client, fhirPatientId, scopeDocId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleExpanded = (key: string): void =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const enterEdit = (group: DocumentGroup): void => {
    const next: Record<string, EditDraft> = {};
    for (const obs of group.observations) {
      if (obs.id) next[obs.id] = draftFromObservation(obs);
    }
    setDrafts(next);
    setEditingKey(group.key);
    // Editing needs the table visible — expand the group if it was collapsed.
    setExpandedKeys((prev) => new Set(prev).add(group.key));
  };

  const cancelEdit = (): void => {
    setEditingKey(null);
    setDrafts({});
  };

  const setDraftField = (
    obsId: string,
    field: Exclude<keyof EditDraft, 'valueKind'>,
    value: string,
  ): void => {
    setDrafts((prev) => {
      const current = prev[obsId];
      if (!current) return prev;
      return { ...prev, [obsId]: { ...current, [field]: value } as EditDraft };
    });
  };

  /**
   * Translate the in-edit drafts for a group into the attest endpoint's edit
   * payload. Only observations actually edited (a draft exists) are included.
   */
  const editsForGroup = (group: DocumentGroup) =>
    group.observations.flatMap((o) => {
      const d = o.id ? drafts[o.id] : undefined;
      if (!o.id || !d) return [];
      const numeric = Number.parseFloat(d.valueNumber);
      return [
        {
          observationId: o.id,
          code: d.code.trim() || null,
          valueQuantity:
            d.valueKind === 'quantity' && !Number.isNaN(numeric)
              ? { value: numeric, unit: d.unit }
              : null,
          valueString: d.valueKind === 'string' ? d.valueString || null : null,
          referenceRange: d.referenceRange || null,
          effectiveDateTime: d.effectiveDateTime || null,
        },
      ];
    });

  /**
   * Approve or reject a report's draft. The frontend sends only intent (report
   * id, decision, any value edits) to the backend attest endpoint — the server
   * flips the statuses and writes the attestation Provenance, attributed to
   * this reviewer. The frontend never writes the provenance itself (path B).
   */
  const reviewGroup = async (
    group: DocumentGroup,
    decision: 'approve' | 'reject',
  ): Promise<void> => {
    setBusyKey(group.key);
    try {
      const res = await apiClient.miniApps.attest({
        params: { tenantId, agentId: AGENT_ID },
        body: {
          diagnosticReportId: group.diagnosticReportId,
          decision,
          ...(decision === 'approve' ? { edits: editsForGroup(group) } : {}),
        },
      });
      if (res.status === 200) {
        const n = res.body.observationIds.length;
        toast.success(
          decision === 'approve'
            ? `Approved ${group.title} (${n} observation${n === 1 ? '' : 's'})`
            : `Rejected ${group.title}`,
        );
        cancelEdit();
        // The attest mutation ran server-side (the backend's user-scoped
        // client), so this client's in-memory request cache still holds the
        // pre-approval report/observations. Drop those cached searches before
        // reloading, or load() would re-render the stale `preliminary` state.
        // Shared client → also refreshes the Files tab's status column.
        client.invalidateSearches('DiagnosticReport');
        client.invalidateSearches('Observation');
        await load();
      } else {
        const errMsg =
          res.body && typeof res.body === 'object' && 'error' in res.body
            ? String((res.body as { error: unknown }).error)
            : 'Unknown error';
        toast.error(
          `${decision === 'approve' ? 'Approval' : 'Rejection'} failed: ${errMsg}`,
        );
      }
    } catch (err) {
      toast.error(
        `${decision === 'approve' ? 'Approval' : 'Rejection'} failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    } finally {
      setBusyKey(null);
    }
  };

  const approveGroup = (group: DocumentGroup): Promise<void> =>
    reviewGroup(group, 'approve');
  const rejectGroup = (group: DocumentGroup): Promise<void> =>
    reviewGroup(group, 'reject');

  if (isLoading && groups.length === 0) {
    return <Text color="tertiary">Loading extracted observations…</Text>;
  }

  if (groups.length === 0) {
    return (
      <Text size="sm" color="tertiary">
        No agent-extracted observations yet. Upload a document and run the
        extraction agent from the Patient Uploads tab.
      </Text>
    );
  }

  return (
    <Stack gap={5} align="stretch" className="w-full">
      {groups.map((group) => {
        const hasObservations = group.observations.length > 0;
        const needsReview = group.observations.some(
          (o) => o.status === 'preliminary',
        );
        const isEditing = editingKey === group.key;
        const isBusy = busyKey === group.key;
        const isExpanded = expandedKeys.has(group.key);
        const dateBits = [
          group.reportDate && `Report ${formatEffectiveDate(group.reportDate)}`,
          group.extractedAt &&
            `Extracted ${formatDate.toDisplay(group.extractedAt)}`,
        ].filter(Boolean) as string[];
        return (
          <section key={group.key} className="w-full">
            {/* Header: title + badges left-aligned (no chevron indent),
                actions next, and a larger expand chevron pinned far right so
                it clearly reads as "open to see more". The title block is
                min-w-0 so it wraps instead of overlapping the actions. */}
            <div className="mb-2 flex w-full items-start gap-2">
              <button
                type="button"
                onClick={() => toggleExpanded(group.key)}
                aria-expanded={isExpanded}
                className="flex min-w-0 flex-1 flex-col gap-1 text-left"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Heading
                    size="lg"
                    weight="semibold"
                    className="min-w-0 break-words"
                  >
                    {group.title}
                  </Heading>
                  <Badge
                    variant="outline"
                    className="shrink-0 whitespace-nowrap"
                  >
                    {group.kind}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="shrink-0 whitespace-nowrap"
                  >
                    {group.observations.length} observation
                    {group.observations.length === 1 ? '' : 's'}
                  </Badge>
                  {hasObservations && (
                    <Badge
                      variant={needsReview ? 'default' : 'success'}
                      className="shrink-0 whitespace-nowrap"
                    >
                      {needsReview ? 'Needs review' : 'Approved'}
                    </Badge>
                  )}
                </div>
                {dateBits.length > 0 && (
                  <Text size="xs" color="tertiary">
                    {dateBits.join(' · ')}
                  </Text>
                )}
              </button>
              {onReview ? (
                // List mode: review happens in the side-by-side modal, not
                // inline — a single Review action opens it for this report's
                // source document.
                needsReview &&
                group.documentReferenceId && (
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => onReview(group.documentReferenceId!)}
                    >
                      Review
                    </Button>
                  </div>
                )
              ) : (
                <>
                  {needsReview && !isEditing && (
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => enterEdit(group)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => rejectGroup(group)}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        disabled={isBusy}
                        onClick={() => approveGroup(group)}
                      >
                        Approve
                      </Button>
                    </div>
                  )}
                  {isEditing && (
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isBusy}
                        onClick={cancelEdit}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={isBusy}
                        onClick={() => approveGroup(group)}
                      >
                        Save &amp; approve
                      </Button>
                    </div>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => toggleExpanded(group.key)}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse report' : 'Expand report'}
                className="mt-0.5 shrink-0 rounded-md p-1 text-text-tertiary hover:bg-muted/60 hover:text-text-primary"
              >
                <Icon
                  name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                  size="xl"
                />
              </button>
            </div>
            {isExpanded && (
              <div className="flex flex-col divide-y divide-border-secondary rounded-md border">
                {group.observations.map((obs) => {
                  const row = toRow(obs);
                  const draft = obs.id ? drafts[obs.id] : undefined;
                  const editable = isEditing && Boolean(draft);
                  if (editable) {
                    return (
                      <div key={row.id} className="flex flex-col gap-2 p-3">
                        <Input
                          value={draft!.code}
                          placeholder="Observation name"
                          onChange={(e) =>
                            setDraftField(obs.id!, 'code', e.target.value)
                          }
                        />
                        {draft!.valueKind === 'quantity' ? (
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              className="w-28"
                              placeholder="value"
                              value={draft!.valueNumber}
                              onChange={(e) =>
                                setDraftField(
                                  obs.id!,
                                  'valueNumber',
                                  e.target.value,
                                )
                              }
                            />
                            <Input
                              className="w-24"
                              placeholder="unit"
                              value={draft!.unit}
                              onChange={(e) =>
                                setDraftField(obs.id!, 'unit', e.target.value)
                              }
                            />
                          </div>
                        ) : (
                          <Input
                            value={draft!.valueString}
                            placeholder="value"
                            onChange={(e) =>
                              setDraftField(
                                obs.id!,
                                'valueString',
                                e.target.value,
                              )
                            }
                          />
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Input
                            className="min-w-0 flex-1"
                            placeholder="Reference range"
                            value={draft!.referenceRange}
                            onChange={(e) =>
                              setDraftField(
                                obs.id!,
                                'referenceRange',
                                e.target.value,
                              )
                            }
                          />
                          <Input
                            type="date"
                            className="w-40"
                            value={draft!.effectiveDateTime.slice(0, 10)}
                            onChange={(e) =>
                              setDraftField(
                                obs.id!,
                                'effectiveDateTime',
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    );
                  }
                  const status = rangeStatus(obs);
                  return (
                    <div
                      key={row.id}
                      className="flex items-start justify-between gap-3 p-3"
                    >
                      <div className="min-w-0">
                        <Text weight="medium" className="break-words">
                          {row.code}
                        </Text>
                        {row.referenceRange && (
                          <Text size="xs" color="tertiary">
                            Ref: {row.referenceRange}
                          </Text>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <div className="flex items-center gap-2">
                          <Text
                            weight="semibold"
                            className={cn(
                              'whitespace-nowrap tabular-nums',
                              status && 'text-destructive',
                            )}
                          >
                            {row.value}
                            {status === 'high'
                              ? ' ↑'
                              : status === 'low'
                                ? ' ↓'
                                : ''}
                          </Text>
                          <ObservationDetailsSheet
                            client={client}
                            row={row}
                            documentTitle={group.title}
                          />
                        </div>
                        {row.effectiveDateTime && (
                          <Text
                            size="xs"
                            color="tertiary"
                            className="whitespace-nowrap"
                          >
                            {formatEffectiveDate(row.effectiveDateTime)}
                          </Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </Stack>
  );
};
